import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Spinner,
} from 'reactstrap';

import { checkDuplicateServiceConnection } from 'services/serviceConnectionService';
import {
  ServiceConnectionBank,
  ServiceConnectionFormValues,
  ServiceProviderDef,
} from 'types/settings/serviceConnectionTypes';
import { CREDENTIAL_MAX_LENGTH } from 'pages/SettingsV2/serviceConnectionConstants';
import SettingsSelect from 'pages/SettingsV2/SettingsSelect';

interface ServiceConnectionModalProps {
  isOpen: boolean;
  provider: ServiceProviderDef | null;
  banks: ServiceConnectionBank[];
  banksLoading: boolean;
  saving: boolean;
  onClose: () => void;
  onSubmit: (values: ServiceConnectionFormValues) => void;
}

const EMPTY_FORM: ServiceConnectionFormValues = {
  merchantId: '',
  keyName: '',
  apiKey: '',
  secretKey: '',
  bankAccountNumber: '',
  bankAccountName: '',
  bankCode: '',
};

type FieldErrors = Partial<Record<keyof ServiceConnectionFormValues, string>>;

const BANK_ACCOUNT_MAX_DIGITS = 15;

const BANK_ACCOUNT_PATTERNS: Record<string, number[]> = {
  SCB: [3, 6, 1],
  SME: [3, 6, 1],
  BBL: [3, 1, 5, 1],
  KBANK: [3, 1, 5, 1],
  KTB: [3, 1, 5, 1],
  BAY: [3, 1, 5, 1],
  TTB: [3, 1, 5, 1],
  CIMB: [3, 1, 5, 1],
  UOB: [3, 1, 5, 1],
  GSB: [3, 1, 5, 1],
};

const getBankAccountPattern = (bankCode: string): number[] =>
  BANK_ACCOUNT_PATTERNS[bankCode] ?? [3, 1, 5, 1];

const toBankDigits = (value: string): string =>
  value.replace(/\D/g, '').slice(0, BANK_ACCOUNT_MAX_DIGITS);

const formatBankAccountNumber = (digits: string, bankCode: string): string => {
  const pattern = getBankAccountPattern(bankCode);
  const parts: string[] = [];
  let cursor = 0;

  pattern.forEach((size) => {
    if (cursor >= digits.length) return;
    parts.push(digits.slice(cursor, cursor + size));
    cursor += size;
  });

  if (cursor < digits.length) {
    parts.push(digits.slice(cursor));
  }

  return parts.filter(Boolean).join('-');
};

const ServiceConnectionModal = ({
  isOpen,
  provider,
  banks,
  banksLoading,
  saving,
  onClose,
  onSubmit,
}: ServiceConnectionModalProps) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<ServiceConnectionFormValues>(EMPTY_FORM);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const duplicateCheckRequestRef = useRef(0);
  const lastDuplicateCheckRef = useRef<{
    merchantId: string;
    providerKey: string;
    isDuplicate: boolean;
  } | null>(null);

  // reset form and errors when modal is opened
  useEffect(() => {
    if (isOpen) {
      setForm(EMPTY_FORM);
      setErrors({});
      setCheckingDuplicate(false);
      duplicateCheckRequestRef.current = 0;
      lastDuplicateCheckRef.current = null;
    }
  }, [isOpen]);

  const setField = (key: keyof ServiceConnectionFormValues, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
    if (key === 'merchantId') {
      lastDuplicateCheckRef.current = null;
    }
  };

  const handleBankAccountNumberChange = (value: string) => {
    const digits = toBankDigits(value);
    setField(
      'bankAccountNumber',
      formatBankAccountNumber(digits, form.bankCode)
    );
  };

  const handleBankCodeChange = (bankCode: string) => {
    setForm((prev) => {
      const digits = toBankDigits(prev.bankAccountNumber);
      return {
        ...prev,
        bankCode,
        bankAccountNumber: formatBankAccountNumber(digits, bankCode),
      };
    });
    setErrors((prev) => ({
      ...prev,
      bankCode: undefined,
      bankAccountNumber: undefined,
    }));
  };

  const runMerchantDuplicateCheck = async (
    merchantIdValue: string
  ): Promise<boolean> => {
    const merchantId = merchantIdValue.trim();
    const providerKey = provider?.key;
    const duplicateMessage = t('This Merchant ID already exists');

    if (!merchantId || !providerKey) {
      return false;
    }

    const cached = lastDuplicateCheckRef.current;
    if (
      cached &&
      cached.merchantId === merchantId &&
      cached.providerKey === providerKey
    ) {
      setErrors((prev) => ({
        ...prev,
        merchantId: cached.isDuplicate
          ? duplicateMessage
          : prev.merchantId === duplicateMessage
            ? undefined
            : prev.merchantId,
      }));
      return cached.isDuplicate;
    }

    const requestId = duplicateCheckRequestRef.current + 1;
    duplicateCheckRequestRef.current = requestId;
    setCheckingDuplicate(true);

    try {
      const isDuplicate = await checkDuplicateServiceConnection(
        providerKey,
        merchantId
      );

      if (duplicateCheckRequestRef.current === requestId) {
        lastDuplicateCheckRef.current = {
          merchantId,
          providerKey,
          isDuplicate,
        };
        setErrors((prev) => ({
          ...prev,
          merchantId: isDuplicate
            ? duplicateMessage
            : prev.merchantId === duplicateMessage
              ? undefined
              : prev.merchantId,
        }));
      }

      return isDuplicate;
    } finally {
      if (duplicateCheckRequestRef.current === requestId) {
        setCheckingDuplicate(false);
      }
    }
  };

  const handleMerchantIdBlur = async () => {
    const merchantId = form.merchantId.trim();
    if (!merchantId) return;
    await runMerchantDuplicateCheck(merchantId);
  };

  const validate = (): boolean => {
    const next: FieldErrors = {};
    const merchantId = form.merchantId.trim();
    const bankAccountDigits = toBankDigits(form.bankAccountNumber);
    const duplicateMessage = t('This Merchant ID already exists');
    const cached = lastDuplicateCheckRef.current;

    if (!merchantId) {
      next.merchantId = t('Please enter Merchant ID');
    } else if (
      cached &&
      cached.merchantId === merchantId &&
      cached.providerKey === provider?.key &&
      cached.isDuplicate
    ) {
      next.merchantId = duplicateMessage;
    }
    if (!form.keyName.trim()) {
      next.keyName = t('Please enter Key Name');
    }
    if (!form.apiKey.trim()) {
      next.apiKey = t('Please enter API Key');
    }
    if (!form.secretKey.trim()) {
      next.secretKey = t('Please enter Secret Key');
    }
    if (!bankAccountDigits) {
      next.bankAccountNumber = t('Please enter Bank Account Number');
    }
    if (!form.bankAccountName.trim()) {
      next.bankAccountName = t('Please enter Bank Account Name');
    }
    if (!form.bankCode.trim()) {
      next.bankCode = t('Please select Bank');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    if (await runMerchantDuplicateCheck(form.merchantId)) return;
    onSubmit(form);
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>
        {provider
          ? `${t('Connect with')} ${provider.name}`
          : t('Add Connection')}
      </ModalHeader>
      <ModalBody>
        <h6 className="text-uppercase text-muted fs-11 mb-3">
          {t('Service Account')}
        </h6>

        <div className="mb-3 row">
          <Label htmlFor="svc-merchant-id" className="col-sm-3 col-form-label">
            {t('Merchant ID')} <span className="text-danger">*</span>
          </Label>
          <Col sm={9}>
            <Input
              id="svc-merchant-id"
              type="text"
              maxLength={CREDENTIAL_MAX_LENGTH}
              value={form.merchantId}
              placeholder={t('Please enter Merchant ID')}
              onChange={(e) => setField('merchantId', e.target.value)}
              onBlur={handleMerchantIdBlur}
            />
            {errors.merchantId ? (
              <div className="text-danger mt-1 err-txt">
                {errors.merchantId}
              </div>
            ) : checkingDuplicate ? (
              <div className="form-text d-flex align-items-center gap-2">
                <Spinner size="sm" />
                <span>{t('Loading...')}</span>
              </div>
            ) : (
              <div className="form-text">
                {t('Merchant ID must not be duplicated')}
              </div>
            )}
          </Col>
        </div>

        <div className="mb-3 row">
          <Label htmlFor="svc-key-name" className="col-sm-3 col-form-label">
            {t('Key Name')} <span className="text-danger">*</span>
          </Label>
          <Col sm={9}>
            <Input
              id="svc-key-name"
              type="text"
              maxLength={CREDENTIAL_MAX_LENGTH}
              value={form.keyName}
              placeholder={t('Please enter Key Name')}
              onChange={(e) => setField('keyName', e.target.value)}
            />
            {errors.keyName ? (
              <div className="text-danger mt-1 err-txt">{errors.keyName}</div>
            ) : null}
          </Col>
        </div>

        <div className="mb-3 row">
          <Label htmlFor="svc-api-key" className="col-sm-3 col-form-label">
            {t('API Key')} <span className="text-danger">*</span>
          </Label>
          <Col sm={9}>
            <Input
              id="svc-api-key"
              type="text"
              maxLength={CREDENTIAL_MAX_LENGTH}
              value={form.apiKey}
              placeholder={t('Please enter API Key')}
              onChange={(e) => setField('apiKey', e.target.value)}
            />
            {errors.apiKey ? (
              <div className="text-danger mt-1 err-txt">{errors.apiKey}</div>
            ) : null}
          </Col>
        </div>

        <div className="mb-3 row">
          <Label htmlFor="svc-secret-key" className="col-sm-3 col-form-label">
            {t('Secret Key')} <span className="text-danger">*</span>
          </Label>
          <Col sm={9}>
            <Input
              id="svc-secret-key"
              type="text"
              maxLength={CREDENTIAL_MAX_LENGTH}
              value={form.secretKey}
              placeholder={t('Please enter Secret Key')}
              onChange={(e) => setField('secretKey', e.target.value)}
            />
            {errors.secretKey ? (
              <div className="text-danger mt-1 err-txt">
                {errors.secretKey}
              </div>
            ) : null}
          </Col>
        </div>

        <hr className="my-4" />

        <h6 className="text-uppercase text-muted fs-11 mb-3">
          {t('Payment Receiving Channel')}
        </h6>

        <div className="mb-3 row">
          <Label htmlFor="svc-bank-acc-no" className="col-sm-3 col-form-label">
            {t('Bank Account Number')} <span className="text-danger">*</span>
          </Label>
          <Col sm={9}>
            <Input
              id="svc-bank-acc-no"
              type="text"
              value={form.bankAccountNumber}
              inputMode="numeric"
              placeholder="000-0-00000-0"
              onChange={(e) => handleBankAccountNumberChange(e.target.value)}
            />
            {errors.bankAccountNumber ? (
              <div className="text-danger mt-1 err-txt">
                {errors.bankAccountNumber}
              </div>
            ) : (
              <div className="form-text">
                {t('Enter up to 15 digits, dashes will be added automatically')}
              </div>
            )}
          </Col>
        </div>

        <div className="mb-3 row">
          <Label
            htmlFor="svc-bank-acc-name"
            className="col-sm-3 col-form-label"
          >
            {t('Bank Account Name')} <span className="text-danger">*</span>
          </Label>
          <Col sm={9}>
            <Input
              id="svc-bank-acc-name"
              type="text"
              maxLength={CREDENTIAL_MAX_LENGTH}
              value={form.bankAccountName}
              onChange={(e) => setField('bankAccountName', e.target.value)}
            />
            {errors.bankAccountName ? (
              <div className="text-danger mt-1 err-txt">
                {errors.bankAccountName}
              </div>
            ) : null}
          </Col>
        </div>

        <div className="mb-0 row">
          <Label htmlFor="svc-bank-code" className="col-sm-3 col-form-label">
            {t('Bank')} <span className="text-danger">*</span>
          </Label>
          <Col sm={9}>
            <SettingsSelect
              inputId="svc-bank-code"
              value={form.bankCode || null}
              onChange={(value) => handleBankCodeChange(String(value))}
              isDisabled={banksLoading}
              placeholder={banksLoading ? t('Loading...') : t('Select Bank')}
              options={banks.map((bank) => ({
                value: bank.code,
                label: bank.nameTh || bank.nameEn || bank.code,
              }))}
            />
            {errors.bankCode ? (
              <div className="text-danger mt-1 err-txt">{errors.bankCode}</div>
            ) : null}
          </Col>
        </div>
      </ModalBody>
      <div className="modal-footer">
        <Button color="light" onClick={onClose} disabled={saving}>
          {t('Cancel')}
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={saving}
          className="d-inline-flex align-items-center gap-1"
        >
          {saving ? <Spinner size="sm" /> : null}
          {t('Save')}
        </Button>
      </div>
    </Modal>
  );
};

export default ServiceConnectionModal;
